const createActionCreator = (type, payloadCreator, metaCreator) => {
    const actionCreator = (...args) => {
        // Not the prettiest way of handling undefined payloadCreator but necessary for backwards compatibility
        const fallback = payload => payload;
        const payload = payloadCreator ? payloadCreator(...args) : fallback(...args);
        let meta;
        if (metaCreator) {
            meta = metaCreator(...args);
        }
        const action = {
            type,
            payload,
            meta,
            error: payload instanceof Error,
        };
        return action;
    };
    actionCreator.toString = () => {
        return type.toString();
    };
    return actionCreator;
};
const internalLogError = (args, isDevelopment) => {
    var _a, _b;
    if (isDevelopment) {
        const errorObject = {
            message: (_a = args[0]) === null || _a === void 0 ? void 0 : _a.message,
            stack: (_b = args[0]) === null || _b === void 0 ? void 0 : _b.stack,
        };
        // tslint:disable-next-line:no-console
        console.error('The failed action-creator was invoked with\n', ...args);
        // tslint:disable-next-line:no-console
        console.error('The following action was created\n', errorObject);
    }
};
const createFailedActionCreator = (isDevelopment, type, payloadCreator, metaCreator) => {
    return (...args) => {
        internalLogError(args, isDevelopment);
        return createActionCreator(type, payloadCreator, metaCreator)(...args);
    };
};
const createActionDescription = (dispatch, type, payloadCreator, metaCreator) => {
    const actionCreator = createActionCreator(type, payloadCreator, metaCreator);
    return {
        type,
        action: actionCreator,
        dispatch: (...args) => dispatch(actionCreator(...args)),
    };
};
/* Takes dispatch argument so actions for any store
 * created by createAction can be dispatched directly
 */
export const makeCreateAction = (dispatch, isDevelopment) => (type, action, success, failed, aborted) => {
    const failedType = `${type}_FAILED`;
    return {
        action: createActionDescription(dispatch, type, action),
        success: createActionDescription(dispatch, `${type}_SUCCESS`, success),
        failed: {
            type: failedType,
            action: createFailedActionCreator(isDevelopment, failedType, failed),
        },
        aborted: createActionDescription(dispatch, `${type}_ABORTED`, aborted),
    };
};
const isActionDescription = (object) => {
    return 'type' in object;
};
/**
 * @deprecated
 * Using this helper makes us lose usage data on which actions are used (via IDE's "Find all references" feature).
 * Dispatch actions via the dispatch function directly instead, e.g. `dispatch(Actions.someAction.action(arg1, arg2, ...))`
 */
export const bindActionCreators = (actions, dispatch) => {
    return Object.keys(actions).reduce((actionCreators, key) => {
        const actionObj = actions[key];
        if (isActionDescription(actionObj)) {
            actionCreators[key] = (...args) => dispatch(actionObj.action(...args));
        }
        else if (typeof actionObj === 'object' && key !== 'default') {
            const actionDescriptionTree = actionObj;
            actionCreators[key] = bindActionCreators(actionDescriptionTree, dispatch);
        }
        return actionCreators;
    }, {});
};
